import {
	Alert,
	Button,
	Checkbox,
	ConfigProvider,
	Flex,
	Input,
	Space,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import {
	CertLineItem,
	CertProposal,
	useConfirmCertProposalMutation,
	useRejectCertProposalMutation,
	useRematchCertProposalMutation,
	useRestoreCertProposalMutation,
	useUndoCertProposalMutation,
} from '../../redux/api/material-certs';
import { BUCKETS, MATCH_METHOD_LABEL, partNumberLabel } from './buckets';
import MatchComparison from './MatchComparison';
import ReassignModal from './ReassignModal';
import SiteTag from './SiteTag';
import VendorTag from './VendorTag';

const { Text } = Typography;

/** Ant Design's success green, so Match reads as the safe, expected action. */
const MATCH_GREEN = '#52c41a';

const PLASTIC_HINTS = ['plastic', 'delrin', 'acetal', 'nylon', 'uhmw', 'peek', 'acrylic'];

const looksLikePlastic = (line: CertLineItem | null | undefined) => {
	if (line?.category_name) return line.category_name.toLowerCase() === 'plastic';
	const typeName = line?.type_name;
	return !!typeName && PLASTIC_HINTS.some((hint) => typeName.toLowerCase().includes(hint));
};

const errorDetail = (error: unknown, fallback: string) =>
	(error as { data?: { detail?: string } })?.data?.detail || fallback;

/**
 * Everything about one heat, plus the buttons that write to ERP.
 *
 * The whole point of the queue is that nothing is attached without someone
 * seeing why the matcher chose a line, so the comparison and the match reasons
 * sit next to the Match button rather than behind a tooltip.
 */
export default function ProposalDetail({
	proposal,
	reassigning,
	setReassigning,
	onNext,
	hasNext,
	onNextPo,
	hasNextPo,
}: {
	proposal: CertProposal;
	reassigning: boolean;
	// Owned by the page so its keyboard shortcuts can stand down while the
	// modal has the reviewer's attention.
	setReassigning: (open: boolean) => void;
	onNext: () => void;
	hasNext: boolean;
	onNextPo: () => void;
	hasNextPo: boolean;
}) {
	const [confirm, { isLoading: confirming }] = useConfirmCertProposalMutation();
	const [reject, { isLoading: rejecting }] = useRejectCertProposalMutation();
	const [restore, { isLoading: restoring }] = useRestoreCertProposalMutation();
	const [undo, { isLoading: undoing }] = useUndoCertProposalMutation();
	const [rematch, { isLoading: rematching }] = useRematchCertProposalMutation();
	// Requested certs arrive with no heat in the subject; the reviewer can read
	// one off the scan and it is saved on the attached certificate.
	const [typedHeat, setTypedHeat] = useState('');
	// Offered on supporting documents, never ticked for the reviewer.
	const [markNoCert, setMarkNoCert] = useState(false);
	useEffect(() => {
		setTypedHeat('');
		setMarkNoCert(false);
	}, [proposal.id, proposal.line_item?.id]);

	const isConfirmed = proposal.status === 'confirmed';
	const isRejected = proposal.status === 'rejected';
	const isMcMaster = proposal.vendor_key === 'mcmaster';
	const isSupporting = proposal.bucket === 'supporting_document';
	// The checkbox is only meaningful once a line is picked and that line has
	// no mill cert. "Pick a line" keeps the same proposal id, so a checkbox
	// left ticked must not ride along onto a line that already has a cert.
	const showNoCertChoice =
		isSupporting &&
		!isConfirmed &&
		!isRejected &&
		!!proposal.line_item &&
		!proposal.line_has_material_certificate &&
		!proposal.line_certificate_not_available;
	const bucket = BUCKETS[proposal.bucket];
	// A supporting document is the packing list itself, so it can be matched
	// once it has a line, even though no certificate will ever arrive.
	const canMatch =
		proposal.bucket === 'ready' || (isSupporting && !!proposal.line_item);
	const askForHeat =
		(proposal.requested || (isMcMaster && proposal.has_certificate_pdf)) &&
		!proposal.heat &&
		!isSupporting;
	const title = isMcMaster
		? `Line ${proposal.line_number ?? '?'}`
		: proposal.heat
			? `HT# ${proposal.heat}`
			: 'Requested cert';
	const barIds = proposal.bar_ids ?? [];
	const materialHref = proposal.material
		? `/material/materials?search=${encodeURIComponent(proposal.material)}`
		: null;

	const run = async (
		action: () => Promise<unknown>,
		success: string,
		fallback: string,
		advance = false
	) => {
		try {
			await action();
			message.success(success);
			if (advance && hasNext) onNext();
		} catch (error) {
			message.error(errorDetail(error, fallback));
		}
	};

	return (
		<Flex vertical gap={12}>
			<Flex justify="space-between" align="start" wrap gap={8}>
				<Space direction="vertical" size={0}>
					<Text strong style={{ fontSize: 16 }}>
						{title}
					</Text>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{proposal.rt && `${proposal.rt} · `}PO {proposal.po}
						{proposal.part_hint && ` · part ${proposal.part_hint}`}
						{proposal.alro_order && ` · order ${proposal.alro_order}`}
						{proposal.packing_slip && ` · slip ${proposal.packing_slip}`}
						{proposal.edp &&
							` · ${partNumberLabel(proposal.vendor_key)} ${proposal.edp}`}
						{proposal.cert_number && ` · cert ${proposal.cert_number}`}
						{proposal.qty && ` · ${proposal.qty} pc`}
					</Text>
				</Space>
				<Space wrap>
					{isConfirmed ? (
						<Tag color="blue">Matched</Tag>
					) : isRejected ? (
						<Tag color="default">Rejected</Tag>
					) : (
						<Tag color={bucket.color}>{bucket.label}</Tag>
					)}
					<VendorTag vendors={[proposal.vendor_key]} />
					{/* The queue mixes both plants, so the site is a standing
					    confirmation rather than something to infer from the PO. */}
					<SiteTag name={proposal.building_name} />
					{proposal.requested && <Tag color="purple">Requested</Tag>}
					{proposal.mixed_heat && <Tag>mixed heats on this line</Tag>}
					{proposal.inventory_exists && <Tag color="cyan">bar received</Tag>}
				</Space>
			</Flex>

			{!isConfirmed && !isRejected && proposal.bucket === 'already_certified' && (
				<Alert
					type="success"
					showIcon
					message="Nothing to do here"
					description="An identical file is already attached in ERP, so this heat is covered. Use Pick another line only if it belongs somewhere else as well."
				/>
			)}

			{!isConfirmed && !isRejected && isSupporting && !proposal.line_item && (
				<Alert
					type="warning"
					showIcon
					message="No ERP line chosen yet"
					description="Pick the ERP line this packing-list line belongs to, then Match attaches the packing list to it."
				/>
			)}

			{!isConfirmed && !isRejected && proposal.bucket !== 'already_certified' && (
				<Alert type="info" showIcon message={bucket.hint} />
			)}

			{isRejected && (
				<Alert
					type="warning"
					showIcon
					message="Rejected"
					description="Nothing was attached in ERP. Restore it to put the heat back in the queue; the match is re-run against ERP as it stands then."
				/>
			)}

			{isConfirmed && (
				<Alert
					type="success"
					showIcon
					message={`Attached${
						proposal.confirmed_by_email ? ` by ${proposal.confirmed_by_email}` : ''
					}${
						proposal.confirmed_at
							? ` on ${dayjs(proposal.confirmed_at).format('MMM D, YYYY h:mm A')}`
							: ''
					}.`}
				/>
			)}

			<MatchComparison proposal={proposal} />

			<Flex justify="space-between" align="start" gap={8} wrap>
				<Space direction="vertical" size={4}>
					{proposal.line_item || proposal.material ? (
						<Space size={12} wrap>
							{proposal.line_item && (
								<Link
									to={`/material/orders/${proposal.material_order}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									Open the ERP order
								</Link>
							)}
							{materialHref && (
								<Link
									to={materialHref}
									target="_blank"
									rel="noopener noreferrer"
								>
									Open Material
								</Link>
							)}
						</Space>
					) : (
						<Text type="warning">No ERP line chosen yet</Text>
					)}
					<BarIds ids={barIds} />
				</Space>
				<Text type="secondary" style={{ fontSize: 12 }}>
					{MATCH_METHOD_LABEL[proposal.match_method] ?? ''}
				</Text>
			</Flex>

			<Text type="secondary" ellipsis style={{ fontSize: 12 }}>
				{proposal.subject || '—'}
			</Text>

			{proposal.mismatch_notes.length > 0 && (
				<Space size={4} wrap>
					<Text type="secondary">Why:</Text>
					{proposal.mismatch_notes.map((note) => (
						<Tag key={note}>{note}</Tag>
					))}
				</Space>
			)}

			{isSupporting &&
				!isConfirmed &&
				!isRejected &&
				!!proposal.line_item &&
				!proposal.line_has_material_certificate && (
				<Space direction="vertical" size={0}>
					{proposal.line_certificate_not_available ? (
						<Text type="secondary">
							This ERP line is already marked: no mill certificate available.
						</Text>
					) : (
						<Checkbox
							checked={markNoCert}
							onChange={(event) => setMarkNoCert(event.target.checked)}
						>
							This material will never have a certificate
						</Checkbox>
					)}
					{showNoCertChoice && !looksLikePlastic(proposal.line_item) && (
							<Text type="warning" style={{ fontSize: 12 }}>
								Metals usually ship with a cert; tick only if the vendor confirmed
								none.
							</Text>
						)}
					<Text type="secondary" style={{ fontSize: 12 }}>
						Common for plastics. Marks the material so the traveler audit and
						the quality packet do not wait for a cert.
					</Text>
				</Space>
			)}

			{isConfirmed && proposal.marked_certificate_not_available && (
				<Text type="secondary">
					Marked as never having a certificate. Undo clears that too.
				</Text>
			)}

			{askForHeat && !isConfirmed && !isRejected && (
				<Space size={8} wrap>
					<Text type="secondary">Heat from the scan (optional):</Text>
					<Input
						size="small"
						style={{ width: 180 }}
						maxLength={64}
						placeholder="e.g. Z00246295"
						value={typedHeat}
						onChange={(event) => setTypedHeat(event.target.value)}
					/>
				</Space>
			)}

			{/* Centred because Match is the one thing the whole panel is for, and
			    a left-aligned row buries it among the escape hatches. */}
			<Flex justify="center" align="center" gap={8} wrap>
				{isConfirmed ? (
					<Button
						danger
						loading={undoing}
						onClick={() =>
							run(
								async () => {
									const result = await undo(proposal.id).unwrap();
									if (result?.packet_layer_sync_failed) {
										message.warning(
											'The material was updated, but an open traveler packet did not refresh. Run the audit again.'
										);
									}
								},
								isSupporting ? 'Packing list detached.' : 'Certificate detached.',
								'Could not undo.'
							)
						}
					>
						Undo
					</Button>
				) : (
					<>
						{/* Green through a scoped theme rather than an inline
						    background, so hover and active states follow. */}
						<ConfigProvider theme={{ token: { colorPrimary: MATCH_GREEN } }}>
							<Button
								type="primary"
								disabled={isRejected || !canMatch}
								loading={confirming}
								onClick={() =>
									run(
										async () => {
											const result = await confirm({
												id: proposal.id,
												heat_lot_number: typedHeat.trim() || undefined,
												mark_certificate_not_available:
													showNoCertChoice && markNoCert ? true : undefined,
											}).unwrap();
											if (result?.certificate_not_available_cleared) {
												message.info(
													'This line was marked "No mill certificate available"; that is now cleared.'
												);
											}
											if (result?.packet_layer_sync_failed) {
												message.warning(
													'The material was updated, but an open traveler packet did not refresh. Run the audit again.'
												);
											}
										},
										isSupporting
											? 'Packing list attached to the ERP line.'
											: 'Certificate attached to the ERP line.',
										isSupporting
											? 'Could not attach the packing list.'
											: 'Could not attach the certificate.',
										// Land on the next heat so a run of matches is one
										// button, not a button and a click back to the list.
										true
									)
								}
							>
								Match
							</Button>
						</ConfigProvider>
						<Button
							// No matched PO is no longer a dead end: the modal can search
							// for one, which is the way through a mistyped number on
							// either side or a PO raised after the cert arrived.
							disabled={
								!proposal.has_certificate_pdf &&
								!(isSupporting && proposal.has_packing_slip_pdf)
							}
							onClick={() => setReassigning(true)}
						>
							{proposal.material_order
								? 'Pick another line'
								: 'Find the PO by hand'}
						</Button>
						<Tooltip title="Re-runs the PO and line match against ERP as it is now. Use it once the PO exists or its lines changed. A line you picked by hand is kept.">
							<Button
								loading={rematching}
								onClick={() =>
									run(
										() => rematch(proposal.id).unwrap(),
										'Match re-run.',
										'Could not re-run the match.'
									)
								}
							>
								Re-check ERP
							</Button>
						</Tooltip>
						{isRejected ? (
							<Button
								loading={restoring}
								onClick={() =>
									run(
										() => restore(proposal.id).unwrap(),
										'Back in the queue and rematched.',
										'Could not restore.'
									)
								}
							>
								Restore to queue
							</Button>
						) : (
							<Button
								danger
								loading={rejecting}
								onClick={() =>
									run(
										() => reject(proposal.id).unwrap(),
										'Rejected. Restore it from the Rejected filter.',
										'Could not reject.'
									)
								}
							>
								Reject
							</Button>
						)}
					</>
				)}
				<Button
					icon={<ArrowRightOutlined />}
					onClick={hasNext ? onNext : onNextPo}
					disabled={!hasNext && !hasNextPo}
				>
					{hasNext ? 'Next' : 'Next PO'}
				</Button>
			</Flex>

			{reassigning && (
				<ReassignModal
					proposal={proposal}
					open={reassigning}
					onClose={() => setReassigning(false)}
				/>
			)}
		</Flex>
	);
}

const BAR_ID_VISIBLE = 4;
const BAR_ID_MAX_CHARS = 10;

function BarIds({ ids }: { ids: string[] }) {
	if (!ids.length) return null;

	const shown = ids.slice(0, BAR_ID_VISIBLE);
	const extra = ids.length - shown.length;

	return (
		<Space size={4} wrap>
			{shown.map((id) => {
				const long = id.length > BAR_ID_MAX_CHARS;
				const label = long ? `${id.slice(0, 3)}…` : id;
				return (
					<Tooltip key={id} title={long ? id : 'Copy bar ID'}>
						<Tag
							style={{ cursor: 'pointer', marginInlineEnd: 0 }}
							onClick={() => {
								void navigator.clipboard.writeText(id);
								message.success(`Copied ${id}`);
							}}
						>
							{label}
						</Tag>
					</Tooltip>
				);
			})}
			{extra > 0 && (
				<Tooltip title={ids.slice(BAR_ID_VISIBLE).join(', ')}>
					<Tag style={{ marginInlineEnd: 0 }}>+{extra}</Tag>
				</Tooltip>
			)}
		</Space>
	);
}
